import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { makeStyles } from "@mui/styles";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";
import Alert from "@mui/material/Alert";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckIcon from "@mui/icons-material/Check";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import StarIcon from "@mui/icons-material/Star";
import DiamondIcon from "@mui/icons-material/Diamond";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import PageShell from "../common/PageShell";
import utils from "../../universal/utils";
import { fetchMembershipStatus } from "../../redux/actions/catalogActions";
import {
  TIERS,
  TIER_PRICES,
  YEARLY_TOTALS,
  BILLING_CYCLES,
  YEARLY_RENEWAL_WINDOW_DAYS,
  YEARLY_RENEWAL_MODE,
  TIER_DISPLAY,
  PLAN_FEATURES,
  PAYMENT_METHODS,
  BANK_TRANSFER_DETAILS,
  CURRENCY,
} from "../../constants/membership";
import { isAllowedPayPalApprovalUrl } from "./membershipPaypalUtils";
import { buildReferralInviteLink } from "../../universal/referralLinks";

const basePath = utils.getEdgeBasePath();

const CURRENCY_SYMBOL = CURRENCY === "EUR" ? "\u20AC" : "$";

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "80vh",
    paddingTop: "2rem",
    paddingBottom: "3rem",
  },
  header: {
    textAlign: "center",
    marginBottom: "1.5rem",
  },
  title: {
    fontWeight: 800,
    fontSize: "2rem",
    color: "#ffffff",
    marginBottom: "0.5rem",
  },
  subtitle: {
    color: "rgba(255, 255, 255, 0.6)",
    fontSize: "1rem",
  },
  topActions: {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    marginBottom: "1rem",
  },
  cycleToggle: {
    display: "flex",
    justifyContent: "center",
    marginBottom: "2rem",
  },
  cardsContainer: {
    display: "flex",
    justifyContent: "center",
    gap: "1.5rem",
    flexWrap: "wrap",
    maxWidth: 900,
    margin: "0 auto",
  },
  card: {
    width: 380,
    borderRadius: 16,
    padding: "2rem",
    position: "relative",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    background: "linear-gradient(180deg, rgba(28, 30, 32, 0.95) 0%, rgba(36, 38, 42, 0.95) 100%)",
  },
  cardHighlighted: {
    border: "1px solid #E8A33D",
    boxShadow: "0 0 30px rgba(232, 163, 61, 0.1)",
  },
  popularBadge: {
    position: "absolute",
    top: -12,
    left: "50%",
    transform: "translateX(-50%)",
  },
  cardTitle: {
    fontWeight: 700,
    fontSize: "1.4rem",
    color: "#ffffff",
    marginBottom: "0.5rem",
  },
  price: {
    fontSize: "2.5rem",
    fontWeight: 800,
    color: "#ffffff",
    marginBottom: "0.25rem",
  },
  priceUnit: {
    fontSize: "1rem",
    fontWeight: 400,
    color: "rgba(255, 255, 255, 0.5)",
  },
  priceSub: {
    fontSize: "0.85rem",
    color: "rgba(255, 255, 255, 0.45)",
    marginBottom: "0.25rem",
  },
  featuresList: {
    listStyle: "none",
    padding: 0,
    margin: "1.5rem 0",
  },
  featureItem: {
    display: "flex",
    alignItems: "flex-start",
    gap: "0.5rem",
    marginBottom: "0.75rem",
  },
  featureText: {
    color: "rgba(255, 255, 255, 0.85)",
    fontSize: "0.9rem",
    lineHeight: 1.4,
  },
  checkIcon: {
    fontSize: "1.1rem",
    marginTop: "2px",
    flexShrink: 0,
  },
  selectButton: {
    width: "100%",
    borderRadius: 10,
    textTransform: "none",
    fontWeight: 700,
    fontSize: "1rem",
    padding: "0.75rem",
  },
  currentPlanLabel: {
    textAlign: "center",
    color: "rgba(255, 255, 255, 0.5)",
    fontSize: "0.85rem",
    marginTop: "1rem",
  },
  bankTransferDialog: {
    "& .MuiDialog-paper": {
      borderRadius: 12,
      backgroundColor: "#1c1e20",
      color: "#ffffff",
    },
  },
  bankDetailRow: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "0.5rem",
    padding: "0.4rem 0",
  },
  bankDetailLabel: {
    color: "rgba(255, 255, 255, 0.6)",
    fontSize: "0.85rem",
  },
  bankDetailValue: {
    color: "#ffffff",
    fontWeight: 600,
    fontSize: "0.85rem",
    textAlign: "right",
  },
}));

export default function PlansPage() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const user = useSelector((state) => state.catalogReducer?.user);
  const membership = useSelector((state) => state.catalogReducer?.membership);
  const queryParams = new URLSearchParams(location.search);
  const requestedBillingCycle = queryParams.get("billingCycle");
  const isYearlyRenewalFlow = queryParams.get("renew") === "yearly";

  const [billingCycle, setBillingCycle] = useState(
    requestedBillingCycle === BILLING_CYCLES.MONTHLY ? BILLING_CYCLES.MONTHLY : BILLING_CYCLES.YEARLY
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [bankTransferData, setBankTransferData] = useState(null);
  const [showBankDialog, setShowBankDialog] = useState(false);
  const [founderProgress, setFounderProgress] = useState(null);
  const [referralCode, setReferralCode] = useState(null);
  const [referralCopied, setReferralCopied] = useState(null);

  useEffect(() => {
    if (user?.userId) {
      dispatch(fetchMembershipStatus(user.userId));
    }
  }, [user?.userId, dispatch]);

  useEffect(() => {
    if (requestedBillingCycle === BILLING_CYCLES.MONTHLY || requestedBillingCycle === BILLING_CYCLES.YEARLY) {
      setBillingCycle(requestedBillingCycle);
    }
  }, [requestedBillingCycle]);

  useEffect(() => {
    fetch(`${basePath}/api/v1/founder/progress`, { credentials: "include" })
      .then((r) => r.json())
      .then((data) => { if (data?.ok) setFounderProgress(data); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!user?.userId) return;
    fetch(`${basePath}/api/v1/referral/my-code`, { credentials: "include" })
      .then((r) => r.json())
      .then((data) => {
        if (data?.ok && (data?.referralCode || data?.code))
          setReferralCode(data.referralCode || data.code);
      })
      .catch(() => {});
  }, [user?.userId]);

  const handleCopyReferral = (text, which) => {
    navigator.clipboard.writeText(text).then(() => {
      setReferralCopied(which);
      setTimeout(() => setReferralCopied(null), 2000);
    }).catch(() => {});
  };

  const currentTier = membership?.tier || TIERS.FREE;
  const isYearly = billingCycle === BILLING_CYCLES.YEARLY;
  const canRenewCurrentYearlyPlan = Boolean(
    isYearlyRenewalFlow
    && isYearly
    && membership?.yearlyRenewalEligible
    && membership?.billingCycle === BILLING_CYCLES.YEARLY
  );

  const handleBack = () => {
    if (window.history.length > 1) {
      history.goBack();
      return;
    }
    history.push("/lobby");
  };

  const handleSubscribe = async (tier, paymentMethod, options = {}) => {
    if (!user?.userId) return;
    setLoading(true);
    setError(null);

    try {
      const renewalMode = options.renewalMode || null;
      const returnParams = new URLSearchParams({
        tier,
        billingCycle,
        paymentMethod,
      });

      if (renewalMode) {
        returnParams.set("renewalMode", renewalMode);
      }

      const body = {
        tier,
        paymentMethod,
        billingCycle,
        renewalMode,
        returnUrl: `${window.location.origin}/membership/success?${returnParams.toString()}`,
        cancelUrl: `${window.location.origin}/membership/cancel?${returnParams.toString()}`,
      };

      const res = await fetch(`${basePath}/api/v1/membership/subscribe`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const data = await res.json();

      if (!data.ok) {
        setError(data.message || "Failed to start subscription");
        setLoading(false);
        return;
      }

      if (paymentMethod === PAYMENT_METHODS.PAYPAL && data.approvalUrl) {
        if (!isAllowedPayPalApprovalUrl(data.approvalUrl)) {
          setError("Received an invalid PayPal approval URL");
          setLoading(false);
          return;
        }

        window.location.assign(data.approvalUrl);
        return;
      }

      if (paymentMethod === PAYMENT_METHODS.BANK_TRANSFER) {
        setBankTransferData(data);
        setShowBankDialog(true);
        setLoading(false);
      }
    } catch (err) {
      setError(err.message || "Network error");
      setLoading(false);
    }
  };

  const plans = [
    {
      tier: TIERS.PREMIUM,
      name: "Premium",
      icon: <StarIcon sx={{ color: "#E8A33D" }} />,
      features: PLAN_FEATURES[TIERS.PREMIUM],
      color: "#E8A33D",
      highlighted: false,
    },
    {
      tier: TIERS.PRO,
      name: "Pro",
      icon: <DiamondIcon sx={{ color: "#FFD700" }} />,
      features: PLAN_FEATURES[TIERS.PRO],
      color: "#FFD700",
      highlighted: true,
    },
  ];

  function getDisplayPrice(tier) {
    return isYearly
      ? TIER_PRICES[tier].yearly
      : TIER_PRICES[tier].monthly;
  }

  function getSavingsPercent(tier) {
    const monthly = TIER_PRICES[tier].monthly;
    const yearly = TIER_PRICES[tier].yearly;
    if (!monthly) return 0;
    return Math.round(((monthly - yearly) / monthly) * 100);
  }

  function getCycleButtonSx(isActive, hasLeftBorder = false) {
    return {
      px: 3,
      py: 1.25,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 0.75,
      minWidth: { xs: 132, sm: 190 },
      fontSize: "0.9rem",
      fontWeight: 700,
      color: isActive ? "#E8A33D" : "rgba(255, 255, 255, 0.72)",
      backgroundColor: isActive ? "rgba(232, 163, 61, 0.15)" : "transparent",
      borderLeft: hasLeftBorder ? "1px solid rgba(255, 255, 255, 0.12)" : "none",
      transition: "background-color 0.15s ease-in-out, color 0.15s ease-in-out",
      "&:hover": {
        backgroundColor: isActive ? "rgba(232, 163, 61, 0.15)" : "rgba(255, 255, 255, 0.04)",
      },
    };
  }

  return (
    <PageShell maxWidth="lg">
      <Box className={classes.root}>
        <Box className={classes.topActions}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{
              textTransform: "none",
              borderColor: "rgba(255, 255, 255, 0.25)",
              color: "rgba(255, 255, 255, 0.85)",
              "&:hover": {
                borderColor: "rgba(255, 255, 255, 0.45)",
                backgroundColor: "rgba(255, 255, 255, 0.06)",
              },
            }}
          >
            Back
          </Button>
          <Button
            variant="text"
            onClick={() => history.push("/lobby")}
            sx={{
              textTransform: "none",
              color: "rgba(255, 255, 255, 0.7)",
              "&:hover": {
                color: "#ffffff",
                backgroundColor: "rgba(255, 255, 255, 0.05)",
              },
            }}
          >
            Go to Lobby
          </Button>
        </Box>

        <Box className={classes.header}>
          <Typography className={classes.title}>Choose Your Plan</Typography>
          <Typography className={classes.subtitle}>
            Unlock premium matchmaking features and elevate your game
          </Typography>
        </Box>

        {isYearlyRenewalFlow && (
          <Alert
            severity={membership?.yearlyRenewalEligible ? "info" : "warning"}
            sx={{ mb: 3, maxWidth: 760, mx: "auto", borderRadius: 2 }}
          >
            {membership?.yearlyRenewalEligible
              ? `Renewing now extends your current yearly membership by another year without losing your remaining time.`
              : `Yearly renewal becomes available during the final ${YEARLY_RENEWAL_WINDOW_DAYS} days before your plan expires.`}
          </Alert>
        )}

        {founderProgress?.spotsAvailable && (
          <Box sx={{ maxWidth: 760, mx: "auto", mb: 3 }}>
            <Alert
              severity="info"
              sx={{
                borderRadius: 2,
                backgroundColor: "rgba(192, 160, 80, 0.08)",
                border: "1px solid rgba(192, 160, 80, 0.2)",
                "& .MuiAlert-icon": { color: "#C0A050" },
              }}
            >
              <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.85)", fontWeight: 600 }}>
                Only {founderProgress.remaining} Founder spots remaining out of {founderProgress.cap}
              </Typography>
            </Alert>
            <LinearProgress
              variant="determinate"
              value={((founderProgress.cap - founderProgress.remaining) / founderProgress.cap) * 100}
              sx={{
                mt: 0.5,
                height: 4,
                borderRadius: 2,
                backgroundColor: "rgba(192, 160, 80, 0.1)",
                "& .MuiLinearProgress-bar": { backgroundColor: "#C0A050" },
              }}
            />
          </Box>
        )}

        {founderProgress?.userIsFounder && (
          <Box sx={{ textAlign: "center", mb: 2 }}>
            <Chip
              label={`You're Founder #${founderProgress.founderNumber}`}
              sx={{
                backgroundColor: "rgba(192, 160, 80, 0.15)",
                color: "#C0A050",
                fontWeight: 700,
                fontSize: "0.85rem",
                border: "1px solid rgba(192, 160, 80, 0.3)",
              }}
            />
          </Box>
        )}

        {/* Billing cycle toggle */}
        <Box className={classes.cycleToggle}>
          <Box
            sx={{
              display: "inline-flex",
              borderRadius: "10px",
              overflow: "hidden",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              backgroundColor: "rgba(255, 255, 255, 0.02)",
            }}
          >
            <ButtonBase
              onClick={() => setBillingCycle(BILLING_CYCLES.MONTHLY)}
              sx={getCycleButtonSx(billingCycle === BILLING_CYCLES.MONTHLY)}
            >
              Monthly
            </ButtonBase>
            <ButtonBase
              onClick={() => setBillingCycle(BILLING_CYCLES.YEARLY)}
              sx={getCycleButtonSx(billingCycle === BILLING_CYCLES.YEARLY, true)}
            >
              Yearly
              <Box
                component="span"
                sx={{
                  height: 20,
                  px: 1,
                  borderRadius: "999px",
                  display: "inline-flex",
                  alignItems: "center",
                  whiteSpace: "nowrap",
                  fontSize: "0.65rem",
                  fontWeight: 700,
                  backgroundColor: "rgba(76, 175, 80, 0.2)",
                  color: "#4CAF50",
                }}
              >
                Save ~31%
              </Box>
            </ButtonBase>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3, maxWidth: 700, mx: "auto" }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box className={classes.cardsContainer}>
          {plans.map((plan) => {
            const isCurrent = currentTier === plan.tier;
            const isDowngrade = currentTier === TIERS.PRO && plan.tier === TIERS.PREMIUM;
            const isRenewCurrentYearlyPlan = isCurrent && canRenewCurrentYearlyPlan;
            const displayPrice = getDisplayPrice(plan.tier);
            const savings = getSavingsPercent(plan.tier);

            return (
              <Box
                key={plan.tier}
                className={`${classes.card} ${plan.highlighted ? classes.cardHighlighted : ""}`}
              >
                {plan.highlighted && (
                  <Chip
                    label="BEST VALUE"
                    size="small"
                    className={classes.popularBadge}
                    sx={{
                      backgroundColor: plan.color,
                      color: "#000",
                      fontWeight: 700,
                      fontSize: "0.7rem",
                    }}
                  />
                )}

                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  {plan.icon}
                  <Typography className={classes.cardTitle}>{plan.name}</Typography>
                </Box>

                {(() => {
                  const isFounderEligible = plan.tier === TIERS.PRO && isYearly
                    && founderProgress?.spotsAvailable && founderProgress?.discountPercent > 0
                    && !founderProgress?.userIsFounder;
                  const founderYearlyTotal = isFounderEligible
                    ? (YEARLY_TOTALS[TIERS.PRO] * (1 - founderProgress.discountPercent / 100)).toFixed(2)
                    : null;
                  const founderMonthly = isFounderEligible
                    ? (founderYearlyTotal / 12).toFixed(2)
                    : null;

                  return isFounderEligible ? (
                    <>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.25 }}>
                        <Typography sx={{
                          fontSize: "1.4rem", fontWeight: 600, textDecoration: "line-through",
                          color: "rgba(255,255,255,0.35)",
                        }}>
                          {CURRENCY_SYMBOL}{displayPrice.toFixed(2)}
                        </Typography>
                        <Chip
                          label="FOUNDER PRICE"
                          size="small"
                          sx={{
                            backgroundColor: "rgba(192, 160, 80, 0.2)",
                            color: "#C0A050",
                            fontWeight: 700,
                            fontSize: "0.6rem",
                            height: 22,
                          }}
                        />
                      </Box>
                      <Typography className={classes.price}>
                        {CURRENCY_SYMBOL}{founderMonthly}
                        <span className={classes.priceUnit}> / month</span>
                      </Typography>
                      <Typography className={classes.priceSub}>
                        Billed as <span style={{ textDecoration: "line-through", marginRight: 4 }}>
                          {CURRENCY_SYMBOL}{YEARLY_TOTALS[plan.tier].toFixed(2)}
                        </span>{" "}
                        <strong style={{ color: "#C0A050" }}>{CURRENCY_SYMBOL}{founderYearlyTotal}</strong>/year
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography className={classes.price}>
                        {CURRENCY_SYMBOL}{displayPrice.toFixed(2)}
                        <span className={classes.priceUnit}> / month</span>
                      </Typography>
                      {isYearly ? (
                        <Typography className={classes.priceSub}>
                          Billed as {CURRENCY_SYMBOL}{YEARLY_TOTALS[plan.tier].toFixed(2)}/year
                        </Typography>
                      ) : (
                        <Typography className={classes.priceSub}>
                          or {CURRENCY_SYMBOL}{TIER_PRICES[plan.tier].yearly.toFixed(2)}/mo with yearly ({savings}% off)
                        </Typography>
                      )}
                    </>
                  );
                })()}

                <Divider sx={{ borderColor: "rgba(255, 255, 255, 0.08)", my: 2 }} />

                <ul className={classes.featuresList}>
                  {plan.features.map((feature, i) => (
                    <li key={i} className={classes.featureItem}>
                      <CheckIcon className={classes.checkIcon} sx={{ color: plan.color }} />
                      <Typography className={classes.featureText}>{feature}</Typography>
                    </li>
                  ))}
                </ul>

                {isRenewCurrentYearlyPlan ? (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <Alert severity="success" sx={{ borderRadius: 2, mb: 0.5 }}>
                      Renew your current {plan.name} yearly plan and extend it by another year.
                    </Alert>
                    <Button
                      variant="contained"
                      className={classes.selectButton}
                      disabled={loading}
                      onClick={() => handleSubscribe(plan.tier, PAYMENT_METHODS.PAYPAL, {
                        renewalMode: YEARLY_RENEWAL_MODE,
                      })}
                      sx={{
                        background: `linear-gradient(135deg, ${plan.color} 0%, ${plan.color}cc 100%)`,
                        color: "#000",
                        "&:hover": {
                          background: `linear-gradient(135deg, ${plan.color}cc 0%, ${plan.color}99 100%)`,
                        },
                      }}
                    >
                      {loading ? (
                        <CircularProgress size={22} sx={{ color: "#000" }} />
                      ) : (
                        "Renew with PayPal"
                      )}
                    </Button>
                    <Button
                      variant="outlined"
                      className={classes.selectButton}
                      disabled={loading}
                      onClick={() => handleSubscribe(plan.tier, PAYMENT_METHODS.BANK_TRANSFER, {
                        renewalMode: YEARLY_RENEWAL_MODE,
                      })}
                      startIcon={<AccountBalanceIcon />}
                      sx={{
                        borderColor: "rgba(255, 255, 255, 0.2)",
                        color: "rgba(255, 255, 255, 0.7)",
                        "&:hover": {
                          borderColor: "rgba(255, 255, 255, 0.4)",
                          backgroundColor: "rgba(255, 255, 255, 0.05)",
                        },
                      }}
                    >
                      Renew with Bank Transfer
                    </Button>
                  </Box>
                ) : isCurrent ? (
                  <Typography className={classes.currentPlanLabel}>Your current plan</Typography>
                ) : (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <Button
                      variant="contained"
                      className={classes.selectButton}
                      disabled={loading}
                      onClick={() => handleSubscribe(plan.tier, PAYMENT_METHODS.PAYPAL)}
                      sx={{
                        background: `linear-gradient(135deg, ${plan.color} 0%, ${plan.color}cc 100%)`,
                        color: "#000",
                        "&:hover": {
                          background: `linear-gradient(135deg, ${plan.color}cc 0%, ${plan.color}99 100%)`,
                        },
                      }}
                    >
                      {loading ? (
                        <CircularProgress size={22} sx={{ color: "#000" }} />
                      ) : isYearly ? (
                        isDowngrade ? "Downgrade with PayPal" : "Pay with PayPal"
                      ) : (
                        "Subscribe with PayPal"
                      )}
                    </Button>

                    {isYearly && (
                      <Button
                        variant="outlined"
                        className={classes.selectButton}
                        disabled={loading}
                        onClick={() => handleSubscribe(plan.tier, PAYMENT_METHODS.BANK_TRANSFER)}
                        startIcon={<AccountBalanceIcon />}
                        sx={{
                          borderColor: "rgba(255, 255, 255, 0.2)",
                          color: "rgba(255, 255, 255, 0.7)",
                          "&:hover": {
                            borderColor: "rgba(255, 255, 255, 0.4)",
                            backgroundColor: "rgba(255, 255, 255, 0.05)",
                          },
                        }}
                      >
                        Bank Transfer
                      </Button>
                    )}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>

        {user && referralCode && (
          <Box sx={{
            maxWidth: 700, mx: "auto", mt: 4, p: 2.5,
            borderRadius: 2,
            backgroundColor: "rgba(255,255,255,0.03)",
            border: "1px solid rgba(255,255,255,0.08)",
            textAlign: "center",
          }}>
            <Typography sx={{ color: "rgba(255,255,255,0.85)", fontWeight: 600, mb: 0.5, fontSize: "0.95rem" }}>
              Know a friend who'd love Ouro?
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.55)", mb: 2, fontSize: "0.85rem" }}>
              Share your personal invite link and get 1 free month for each friend who subscribes.
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "center", gap: 1.5, flexWrap: "wrap" }}>
              <Box sx={{
                display: "flex", alignItems: "center", gap: 0.5,
                px: 1.5, py: 0.75, borderRadius: 1.5,
                backgroundColor: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.1)",
              }}>
                <Typography variant="body2" sx={{ color: "#fff", fontWeight: 600, fontSize: "0.85rem" }}>
                  {referralCode}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => handleCopyReferral(referralCode, "code")}
                  sx={{ color: referralCopied === "code" ? "#4CAF50" : "rgba(255,255,255,0.5)", p: 0.5 }}
                >
                  {referralCopied === "code" ? <CheckIcon sx={{ fontSize: 16 }} /> : <ContentCopyIcon sx={{ fontSize: 16 }} />}
                </IconButton>
              </Box>
              <Box sx={{
                display: "flex", alignItems: "center", gap: 0.5,
                px: 1.5, py: 0.75, borderRadius: 1.5,
                backgroundColor: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.1)",
              }}>
                <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.8rem" }}>
                  {buildReferralInviteLink(referralCode)}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => handleCopyReferral(buildReferralInviteLink(referralCode), "link")}
                  sx={{ color: referralCopied === "link" ? "#4CAF50" : "rgba(255,255,255,0.5)", p: 0.5 }}
                >
                  {referralCopied === "link" ? <CheckIcon sx={{ fontSize: 16 }} /> : <ContentCopyIcon sx={{ fontSize: 16 }} />}
                </IconButton>
              </Box>
            </Box>
          </Box>
        )}

        {/* Bank Transfer Instructions Dialog */}
        <Dialog
          open={showBankDialog}
          onClose={() => setShowBankDialog(false)}
          className={classes.bankTransferDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 700 }}>Bank Transfer Instructions</DialogTitle>
          <DialogContent>
            {bankTransferData && (
              <>
                <Typography sx={{ mb: 2, color: "rgba(255, 255, 255, 0.7)", fontSize: "0.9rem" }}>
                  Please transfer the amount below using the details provided. Include the
                  reference code so we can match your payment.
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <Box className={classes.bankDetailRow}>
                    <Typography className={classes.bankDetailLabel}>Amount</Typography>
                    <Typography className={classes.bankDetailValue}>
                      {CURRENCY_SYMBOL}{bankTransferData.amount?.toFixed(2)} {bankTransferData.currency}
                    </Typography>
                  </Box>
                  <Divider sx={{ borderColor: "rgba(255, 255, 255, 0.08)" }} />
                  <Box className={classes.bankDetailRow}>
                    <Typography className={classes.bankDetailLabel}>Account Holder</Typography>
                    <Typography className={classes.bankDetailValue}>{BANK_TRANSFER_DETAILS.accountHolder}</Typography>
                  </Box>
                  <Box className={classes.bankDetailRow}>
                    <Typography className={classes.bankDetailLabel}>IBAN</Typography>
                    <Typography className={classes.bankDetailValue}>{BANK_TRANSFER_DETAILS.iban}</Typography>
                  </Box>
                  <Box className={classes.bankDetailRow}>
                    <Typography className={classes.bankDetailLabel}>SWIFT/BIC</Typography>
                    <Typography className={classes.bankDetailValue}>{BANK_TRANSFER_DETAILS.bic}</Typography>
                  </Box>
                  <Box className={classes.bankDetailRow}>
                    <Typography className={classes.bankDetailLabel}>Bank Name and Address</Typography>
                    <Typography className={classes.bankDetailValue}>
                      {[BANK_TRANSFER_DETAILS.bankName, BANK_TRANSFER_DETAILS.bankAddress]
                        .filter(Boolean).join(", ")}
                    </Typography>
                  </Box>
                  <Divider sx={{ borderColor: "rgba(255, 255, 255, 0.08)" }} />
                  <Box className={classes.bankDetailRow} sx={{ mt: 1 }}>
                    <Typography className={classes.bankDetailLabel}>Reference Code</Typography>
                    <Chip
                      label={bankTransferData.referenceCode}
                      sx={{
                        fontWeight: 700,
                        backgroundColor: "rgba(232, 163, 61, 0.15)",
                        color: "#E8A33D",
                        fontSize: "0.9rem",
                      }}
                    />
                  </Box>
                </Box>

                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  Your membership will be activated once we confirm receipt of your payment.
                  This usually takes 1-3 business days.
                </Alert>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowBankDialog(false)} sx={{ color: "rgba(255, 255, 255, 0.7)" }}>
              Close
            </Button>
            <Button onClick={() => history.push("/account")} variant="contained" sx={{ borderRadius: 2 }}>
              Back to Account
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </PageShell>
  );
}
